import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Megaphone, X } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { cn } from '@/lib/utils';

/**
 * Phase 48: 활성 공지 배너 — AppLayout 상단에 노출.
 *
 * - 로그인 회원만 fetch (인증 라우트)
 * - 한 번 닫으면 그 세션에는 다시 안 뜸 (localStorage 에 dismissed id 저장)
 */
const DISMISSED_KEY = 'keywordgod.announcements.dismissed';

function readDismissed(): number[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(DISMISSED_KEY);
    return raw ? (JSON.parse(raw) as number[]).filter((n) => typeof n === 'number') : [];
  } catch {
    return [];
  }
}
function writeDismissed(ids: number[]) {
  try {
    window.localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids.slice(-50)));
  } catch {
    /* ignore */
  }
}

export function AnnouncementBanner() {
  const user = useAuthStore((s) => s.user);
  const [dismissed, setDismissed] = useState<number[]>(() => readDismissed());

  const q = useQuery({
    queryKey: ['announcements', 'active'],
    queryFn: () => api.announcements.active(),
    enabled: !!user,
    staleTime: 60_000,
    refetchInterval: 5 * 60 * 1000,
  });

  useEffect(() => {
    writeDismissed(dismissed);
  }, [dismissed]);

  if (!user || !q.data || q.data.length === 0) return null;
  const visible = q.data.filter((a) => !dismissed.includes(a.id));
  if (visible.length === 0) return null;

  return (
    <div className="space-y-1.5 px-4 sm:px-6 pt-3">
      {visible.map((a) => {
        const cls = {
          info: 'border-blue-400/40 bg-blue-400/10 text-blue-100',
          event: 'border-purple-400/40 bg-purple-400/10 text-purple-100',
          warn: 'border-amber-400/50 bg-amber-400/15 text-amber-100',
        }[a.level];
        return (
          <div
            key={a.id}
            className={cn(
              'mx-auto max-w-7xl rounded-lg border px-4 py-2.5 flex items-start gap-3',
              cls,
            )}
          >
            <Megaphone className="h-4 w-4 shrink-0 mt-0.5 opacity-80" />
            <div className="flex-1 min-w-0 text-sm">
              <strong className="font-bold">{a.title}</strong>
              <span className="ml-2 opacity-90 whitespace-pre-wrap">{a.body}</span>
            </div>
            <button
              type="button"
              onClick={() => setDismissed([...dismissed, a.id])}
              className="text-current opacity-60 hover:opacity-100 shrink-0"
              aria-label="닫기"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
